import { P, Predicate } from "breadcommon";

export interface TransactionColumnFilterDisplayTypeProps {
  predicate: Predicate;
}

export function TransactionColumnFilterDisplayType({
  predicate,
}: TransactionColumnFilterDisplayTypeProps): JSX.Element {
  // Helper function to format a list with "or" between the last two items
  const formatListWithOr = (items: string[]): string => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} or ${items[1]}`;
    return `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
  };

  // Parse the predicate to extract type and subtype filters
  // Expected structure: P.All(P.Any(...typePredicates), P.Any(...subtypePredicates))
  // or just P.Any(...typePredicates) or P.Any(...subtypePredicates)
  const serializable = predicate.asSerializable();

  const extractValuesFromAny = (anyPredicate: any): string[] => {
    if (anyPredicate.name === P.OpName.Any && anyPredicate.args) {
      return anyPredicate.args
        .filter(
          (arg: any) =>
            arg.name === P.OpName.Equals &&
            arg.args.length === 2 &&
            arg.args[1].name === P.OpName.Literal,
        )
        .map((arg: any) => arg.args[1].args[0])
        .filter((value: any) => typeof value === "string");
    }
    return [];
  };

  const extractFieldName = (anyPredicate: any): string | null => {
    if (
      anyPredicate.name === P.OpName.Any &&
      anyPredicate.args &&
      anyPredicate.args.length > 0
    ) {
      const firstArg = anyPredicate.args[0];
      if (firstArg.name === P.OpName.Equals && firstArg.args.length === 2) {
        const fieldArg = firstArg.args[0];
        if (fieldArg.name === P.OpName.Field && fieldArg.args.length === 1) {
          return fieldArg.args[0];
        }
      }
    }
    return null;
  };

  let types: string[] = [];
  let subtypes: string[] = [];

  if (serializable.name === P.OpName.All && serializable.args.length > 0) {
    // Multiple conditions in P.All - could be type and subtype
    for (const arg of serializable.args) {
      const fieldName = extractFieldName(arg);
      const values = extractValuesFromAny(arg);

      if (fieldName === "type") {
        types = values;
      } else if (fieldName === "subtype") {
        subtypes = values;
      }
    }
  } else if (serializable.name === P.OpName.Any) {
    // Single Any condition - determine if it's type or subtype
    const fieldName = extractFieldName(serializable);
    const values = extractValuesFromAny(serializable);

    if (fieldName === "type") {
      types = values;
    } else if (fieldName === "subtype") {
      subtypes = values;
    }
  }

  // Format the display text
  const parts: string[] = [];

  if (types.length > 0) {
    if (types.length === 1) {
      parts.push(`Type is ${types[0]}`);
    } else if (types.length <= 3) {
      parts.push(`Type is ${formatListWithOr(types)}`);
    } else {
      parts.push(
        `Type is ${types.slice(0, 2).join(", ")}, or ${types.length - 2} more`,
      );
    }
  }

  if (subtypes.length > 0) {
    if (subtypes.length === 1) {
      parts.push(`Subtype is ${subtypes[0]}`);
    } else if (subtypes.length <= 3) {
      parts.push(`Subtype is ${formatListWithOr(subtypes)}`);
    } else {
      parts.push(
        `Subtype is ${subtypes.slice(0, 2).join(", ")}, or ${subtypes.length - 2} more`,
      );
    }
  }

  if (parts.length === 0) {
    return <span>Type filter</span>;
  }

  return <span>{parts.join(" and ")}</span>;
}
